// TrainingsReports.js

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import {
  Progress,
  Table,
  Checkbox,
  Rate,
  message,
} from 'antd';

import {
  DownloadOutlined,
} from '@ant-design/icons';

import {
  useLocation,
} from 'react-router-dom';

import * as XLSX from 'xlsx';

import jsPDF from 'jspdf';

import autoTable from 'jspdf-autotable';

import { TrainingsApi } from '../../Api/TrainingsApi';
import { toastSuccess, toastError, toastWarn } from '../Toast/ToastComponent';

import TraineeFeedbackDownload from './TraineeFeedbackDownload';

import { downloadTrainingsExcel } from '../ReusableComponents/downloadtraningsexcel';

const TrainingsReports = forwardRef(
  (
    {
      onRegisterExcel,
      onRegisterPDF,
    },
    ref
  ) => {
    const location =
      useLocation();

    // ================= GET TRAINING ID =================

    const overviewData =
      location?.state
        ?.overviewData || {};

    const trainingId =
      overviewData
        ?.training_details
        ?.id;

    // ================= STATES =================

    const [loading, setLoading] =
      useState(false);

    const [
      tableData,
      setTableData,
    ] = useState([]);

    const [
      trainingName,
      setTrainingName,
    ] = useState('');

    const [dynamicHeaders, setDynamicHeaders] = useState([]);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // Keep a ref so export callbacks always read the latest selectedRowKeys
    const selectedRowKeysRef = useRef([]);
    useEffect(() => {
      selectedRowKeysRef.current = selectedRowKeys;
    }, [selectedRowKeys]);

    // ================= FEEDBACK POPUP =================

    const [
      feedbackPopup,
      setFeedbackPopup,
    ] = useState(false);

    const [
      selectedTrainee,
      setSelectedTrainee,
    ] = useState(null);

    // ================= API =================

    useEffect(() => {
      if (trainingId) {
        getReports();
      }
    }, [trainingId]);

    const getReports =
      async () => {
        try {
          setLoading(true);

          const payload = {
            training_id:
              trainingId,
          };

          const response =
            await TrainingsApi.getTrainingReport(
              payload
            );

          if (
            response.data.success
          ) {
            const result =
              response.data
                .result;

            setTrainingName(
              result?.training_name
            );

            setDynamicHeaders(
              result?.dynamic_headers || []
            );

            const mappedData =
              (
                result?.reports ||
                []
              ).map(
                (
                  item,
                  index
                ) => ({
                  ...item,
                  key:
                    index + 1,

                  trainee_id:
                    item.trainee_id,

                  employee_code:
                    item.employee_code,

                  completion_date:
                    item.completion_date,

                  trainee_name:
                    item.trainee_name,

                  department_name:
                    item.department_name,

                  trainers:
                    item.trainers,

                  attendance_percentage:
                    typeof item.attendance_percentage === 'string' 
                      ? Number(item.attendance_percentage.replace('%', '')) 
                      : item.attendance_percentage,

                  rating:
                    item.rating,
                })
              );

            setTableData(
              mappedData
            );
          } else {
            message.error(
              response.data
                .message
            );
          }
        } catch (error) {
          console.log(error);

          message.error(
            'Failed to fetch reports'
          );
        } finally {
          setLoading(false);
        }
      };

    // ================= EXPORT EXCEL =================

    const handleExportExcel = () => {
      const keys = selectedRowKeysRef.current;
      const dataToExport =
        keys.length > 0
          ? tableData.filter((row) => keys.includes(row.key))
          : tableData;

      downloadTrainingsExcel({
        data: dataToExport,
        dynamicHeaders: dynamicHeaders,
        fileName: `${trainingName}_Report.xlsx`,
        sheetName: 'Training Report'
      });
    };

    // ================= EXPORT PDF =================

    const handleExportPDF = () => {
      const keys = selectedRowKeysRef.current;
      const dataToExport =
        keys.length > 0
          ? tableData.filter((row) => keys.includes(row.key))
          : tableData;

      const doc = new jsPDF('landscape');

      doc.setFontSize(18);
      doc.text(`${trainingName} Report`, 14, 18);

      autoTable(doc, {
        startY: 28,
        head: [
          [
            'S.NO',
            'TRAINEE NAME',
            'DEPARTMENT',
            'TRAINER(S)',
            'ATTENDANCE %',
            'AVG FEEDBACK',
          ],
        ],
        body: dataToExport.map((item, index) => [
          index + 1,
          item.trainee_name || '-',
          item.department_name || '-',
          item.trainers || '--',
          `${item.attendance_percentage}%`,
          `${item.rating} / 5`,
        ]),
        styles: { fontSize: 10, halign: 'center', valign: 'middle' },
        headStyles: {
          fillColor: [233, 236, 239],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
        },
      });

      doc.save(`${trainingName}_Report.pdf`);
    };

    // ================= REGISTER EXPORT =================

    useEffect(() => {
      if (
        onRegisterExcel
      ) {
        onRegisterExcel(
          () =>
            handleExportExcel()
        );
      }

      if (
        onRegisterPDF
      ) {
        onRegisterPDF(
          () =>
            handleExportPDF()
        );
      }
    }, [tableData]);

    // ================= IMPERATIVE HANDLE =================

    useImperativeHandle(
      ref,
      () => ({
        exportExcel:
          handleExportExcel,

        exportPDF:
          handleExportPDF,
      })
    );

    // ================= TABLE COLUMNS =================

    const columns = [
    //   {
    //     title:
    //       <Checkbox />,

    //     render: () => (
    //       <Checkbox />
    //     ),

    //     width: 50,
    //   },
  
      {
        title:
          'TRAINEE NAME',

        dataIndex:
          'trainee_name',
      },

      {
        title:
          'DEPARTMENT',

        dataIndex:
          'department_name',
      },

      {
        title:
          'TRAINER(S)',

        dataIndex:
          'trainers',
      },

      {
        title:
          'ATTENDANCE %',

        dataIndex:
          'attendance_percentage',

        render: (
          value
        ) => (
          <div
            className="d-flex align-items-center gap-2"
          >
            <Progress
              percent={
                value
              }
              showInfo={
                false
              }
              strokeColor={
                value >= 75
                  ? '#52c41a'
                  : value >= 40
                  ? '#faad14'
                  : '#ff4d4f'
              }
              style={{
                width: 100,
              }}
            />

            <span>
              {value}%
            </span>
          </div>
        ),
      },

      {
        title:
          'AVG FEEDBACK',

        dataIndex:
          'rating',

        render: (
          value
        ) => (
          <div
            className="d-flex align-items-center gap-2"
          >
            <Rate
              disabled
              allowHalf
              value={
                value
              }
            />

            <span>
              <b>
                {value}
              </b>{' '}
              / 5
            </span>
          </div>
        ),
      },

      {
        title:
          'DOWNLOAD',

        render: (
          _,
          record
        ) => (
          <DownloadOutlined
            style={{
              fontSize: 22,
              cursor:
                'pointer',
            }}
            onClick={async (
              e
            ) => {
              e.stopPropagation();

              try {
                const payload = {
                  training_id: trainingId,
                  trainee_id: record.trainee_id,
                };
                
                const response = await TrainingsApi.getTraineeFeedbackDownload(payload);
                
                if (response.data.success) {
                  setSelectedTrainee(record);
                  setFeedbackPopup(true);
                } else {
                  toastWarn(response.data.message || "No data available");
                }
              } catch (error) {
                toastError("Failed to check data availability");
              }
            }}
          />
        ),
      },
    ];

    return (
      <div className="container-fluid p-2">
        {/* ================= TOP ================= */}

        <div className="mb-4">
          <h5 className="mb-0">
            Training Summary Report
          </h5>
        </div>

        {/* ================= TABLE ================= */}

        <Table
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          columns={columns}
          dataSource={
            tableData
          }
          pagination={false}
        />

        {/* ================= POPUP ================= */}

        <TraineeFeedbackDownload
          open={
            feedbackPopup
          }
          onClose={() =>
            setFeedbackPopup(
              false
            )
          }
          trainingId={
            trainingId
          }
          traineeId={
            selectedTrainee?.trainee_id
          }
        />
      </div>
    );
  }
);

export default TrainingsReports;